package com.shadowings.gero.presentation.component

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DevicePreviews
import compose.icons.FeatherIcons
import compose.icons.feathericons.Lock
import gero.composeapp.generated.resources.A2
import gero.composeapp.generated.resources.A28
import gero.composeapp.generated.resources.A34
import gero.composeapp.generated.resources.A36
import gero.composeapp.generated.resources.A8
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.app_name
import gero.composeapp.generated.resources.bar_title_builder
import gero.composeapp.generated.resources.bar_title_builder_short
import gero.composeapp.generated.resources.bar_title_chapters
import gero.composeapp.generated.resources.bar_title_chapters_short
import gero.composeapp.generated.resources.bar_title_compendio
import gero.composeapp.generated.resources.bar_title_compendio_short
import gero.composeapp.generated.resources.bar_title_personaggi
import gero.composeapp.generated.resources.bar_title_personaggi_short
import gero.composeapp.generated.resources.bar_title_profile
import gero.composeapp.generated.resources.bar_title_profile_short
import gero.composeapp.generated.resources.locked_icon_desc
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

private val WebDrawerWidth = 260.dp

// Stesso ordine di BottomBar/LeftNavigationBar: 0 Impara (Capitoli/Correggi come tab interne),
// 1 Compendio, 2 Builder, 3 Personaggi, 4 Profilo.
private fun isSectionLocked(index: Int, starCount: Int): Boolean {
    return when (index) {
        1 -> starCount < 1   // Compendio needs 1 star
        2 -> starCount < 30  // Builder needs 30 stars
        3 -> starCount < 10  // Personaggi needs 10 stars
        else -> false
    }
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun WebNavigationDrawerPreview() {
    AppTheme {
        Surface {
            WebNavigationDrawer(
                selectedIndex = 0,
                isLoggedIn = false,
                language = "it",
                starCount = 18,
                onItemSelected = {}
            )
        }
    }
}

/**
 * Persistent left navigation drawer used on wide web viewports (see [ChaptersScreen]'s
 * `isWideWebLayout`) in place of the [LeftNavigationBar] used on landscape phones/tablets.
 * Stats (level/streak/stars/hearts) live in [TopBar] instead, same as every other layout —
 * a previous version duplicated them here with large icons, which pushed the nav items below
 * the fold on short browser windows (no scroll fallback existed either).
 */
@Composable
fun WebNavigationDrawer(
    selectedIndex: Int,
    isLoggedIn: Boolean,
    language: String,
    starCount: Int,
    onItemSelected: (Int) -> Unit
) {
    val hapticFeedback = LocalHapticFeedback.current
    key(language, isLoggedIn, starCount) {
        val useShort = LocalDensity.current.fontScale > 1.49f
        // Larger than LeftNavigationBar/BottomBar's compact rail icons — this drawer has much
        // more room, and the small size made the hieroglyph artwork look pixelated.
        val iconSize = if (useShort) 52.dp else 64.dp
        val sections: List<Pair<String, DrawableResource>> = listOf(
            stringResource(if (useShort) Res.string.bar_title_chapters_short else Res.string.bar_title_chapters) to Res.drawable.A28,
            stringResource(if (useShort) Res.string.bar_title_compendio_short else Res.string.bar_title_compendio) to Res.drawable.A8,
            stringResource(if (useShort) Res.string.bar_title_builder_short else Res.string.bar_title_builder) to Res.drawable.A34,
            stringResource(if (useShort) Res.string.bar_title_personaggi_short else Res.string.bar_title_personaggi) to Res.drawable.A36,
            stringResource(if (useShort) Res.string.bar_title_profile_short else Res.string.bar_title_profile) to Res.drawable.A2,
        )

        Column(
            modifier = Modifier
                .width(WebDrawerWidth)
                .fillMaxHeight()
                .background(MaterialTheme.colorScheme.background)
                .semantics { testTag = "web_navigation_drawer" }
        ) {
            Text(
                text = stringResource(Res.string.app_name),
                style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                color = MaterialTheme.colorScheme.onBackground,
                modifier = Modifier.padding(horizontal = 16.dp, vertical = 16.dp)
            )
            HorizontalDivider()

            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .weight(1f)
                    .verticalScroll(rememberScrollState())
                    .padding(vertical = 8.dp)
            ) {
                sections.forEachIndexed { index, pair ->
                    val isLocked = isSectionLocked(index, starCount)
                    val tintColor = if (isLocked) {
                        MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                    } else {
                        MaterialTheme.colorScheme.onSurface
                    }
                    val backgroundColor = if (selectedIndex == index) {
                        MaterialTheme.colorScheme.secondaryContainer
                    } else {
                        MaterialTheme.colorScheme.background
                    }
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 4.dp)
                            .background(backgroundColor, RoundedCornerShape(12.dp))
                            .clickable {
                                hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                                onItemSelected(index)
                            }
                            .padding(horizontal = 12.dp, vertical = 10.dp)
                            .semantics { testTag = "web_drawer_item_${pair.first.lowercase()}" },
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Box {
                            if (index == 4 && !isLoggedIn && !isLocked) {
                                BadgedBox(badge = { Badge() }) {
                                    Image(
                                        modifier = Modifier.size(iconSize),
                                        painter = painterResource(pair.second),
                                        contentDescription = pair.first,
                                        colorFilter = ColorFilter.tint(tintColor)
                                    )
                                }
                            } else {
                                Image(
                                    modifier = Modifier.size(iconSize),
                                    painter = painterResource(pair.second),
                                    contentDescription = pair.first,
                                    colorFilter = ColorFilter.tint(tintColor)
                                )
                            }
                            if (isLocked) {
                                Icon(
                                    imageVector = FeatherIcons.Lock,
                                    contentDescription = stringResource(Res.string.locked_icon_desc),
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier
                                        .size(16.dp)
                                        .align(Alignment.BottomEnd)
                                        .background(
                                            color = MaterialTheme.colorScheme.surface,
                                            shape = androidx.compose.foundation.shape.CircleShape
                                        )
                                        .padding(2.dp)
                                )
                            }
                        }
                        Text(
                            text = pair.first,
                            color = tintColor,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        }
    }
}
